import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store'

import Envelope from '@/views/Envelope'
import PageError from '@/views/PageError'
import Download from '@/views/Download'
import EnvelopeComplete from '@/views/EnvelopeComplete'
import EnvelopeSigned from '@/views/EnvelopeSigned'
import WidgetSigned from '@/views/WidgetSigned'
import WidgetVerify from '@/views/WidgetVerify'

import TailwindVars from '@/components/TailwindVars'
import OffsetColorBoxDesign from '@/components/designs/OffsetColorBoxDesign'
import CtaSectionDesign from '@/components/designs/CtaSectionDesign'
import PostSigningScreenDesign from '@/components/designs/PostSigningScreenDesign'

Vue.use(Router)

const routes = [
  {
    path: '/error',
    name: 'PageError',
    component: PageError,
    props: true,
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/preview/bulk-send/:bulkSendImportedContactFingerprint',
    name: 'BulkSendPreview',
    component: Envelope,
    meta: {
      requiresAuth: false,
      requiresPreviewAuth: false
    }
  },
  {
    path: '/preview/:envelopeFingerprint/:signatureFingerprint',
    name: 'Preview',
    component: Envelope,
    meta: {
      requiresAuth: false,
      requiresPreviewAuth: true
    }
  },
  {
    path: '/preview/:previewURL',
    name: 'OldPreview',
    meta: {
      requiresAuth: false
    },
    beforeEnter (from) {
      window.location = process.env.VUE_APP_SECONDARY_SIGNING_PAGE_URL + '/#' + from.fullPath
    }
  },
  {
    path: '/widget/:widgetCode',
    name: 'SignWidget',
    meta: {
      requiresAuth: false
    },
    component: Envelope
  },
  {
    path: '/widget-complete/:widgetCode/:status',
    name: 'WidgetComplete',
    meta: {
      requiresAuth: false
    },
    component: EnvelopeComplete,
    props: (route) => ({ status: route.params.status })
  },
  {
    path: '/widget-signed/:widgetCode',
    name: 'WidgetSigned',
    props: true,
    meta: {
      requiresAuth: false
    },
    component: WidgetSigned
  },
  {
    path: '/widget-verify/:envelopeFingerprint/:widgetCode',
    name: 'WidgetVerify',
    meta: {
      requiresAuth: false
    },
    component: WidgetVerify,
    beforeEnter (to, _, next) {
      // widget-verify uses laravel signed urls
      // which require signature and expires query params
      if (!to.query.signature || !to.query.expires) {
        next({ name: 'PageError', params: { errorMessage: 'Page not found', errorCode: 404 } })
      }

      next()
    }
  },
  {
    path: '/:envelopeFingerprint/:signatureFingerprint',
    name: 'OldSign',
    meta: {
      requiresAuth: false
    },
    beforeEnter (from) {
      window.location = process.env.VUE_APP_SECONDARY_SIGNING_PAGE_URL + '/#' + from.fullPath
    }
  },
  {
    path: '/complete',
    name: 'EnvelopeComplete',
    props: true,
    meta: {
      requiresAuth: true
    },
    component: EnvelopeComplete
  },
  {
    path: '/signed',
    name: 'EnvelopeSigned',
    meta: {
      requiresAuth: true
    },
    component: EnvelopeSigned
  },
  {
    path: '/download',
    name: 'Download',
    meta: {
      requiresAuth: true
    },
    component: Download
  },
  {
    path: '/',
    name: 'Sign',
    meta: {
      requiresAuth: true
    },
    component: Envelope
  },
  {
    path: '*',
    name: 'notFound',
    meta: {
      requiresAuth: false
    },
    beforeEnter (to, from, next) {
      next({ name: 'PageError', params: { errorMessage: 'Page not found', errorCode: 404 } })
    }
  }
]

if (process.env.VUE_APP_ENV !== 'production') {
  routes.push({
    path: '/tailwind',
    name: 'Tailwind',
    meta: {
      requiresAuth: false
    },
    component: TailwindVars
  })
  routes.unshift({
    path: '/designs/offset_color_box',
    name: 'OffsetColorBoxDesign',
    meta: {
      requiresAuth: false
    },
    component: OffsetColorBoxDesign
  })
  routes.unshift({
    path: '/designs/cta_section',
    name: 'CtaSectionDesign',
    meta: {
      requiresAuth: false
    },
    component: CtaSectionDesign
  })
  routes.unshift({
    path: '/designs/post_signing',
    name: 'PostSigningScreenDesign',
    meta: {
      requiresAuth: false
    },
    component: PostSigningScreenDesign
  })
}

const router = new Router({
  mode: 'history',
  routes
})

router.beforeEach((to, from, next) => {
  if (to.name === 'PageError') {
    next()
    return
  }

  if (to.hash.startsWith('#/') && to.path === '/') { // Handle paths beginning with /#/ gracefully
    next({ path: to.hash.substring(1) })
    return
  }

  // If the route requires authentication and not authenticated
  if (to.meta.requiresAuth && !store.getters['auth/isAuthenticated']) {
    // Get the key from the URL
    let key = to.hash.substring(1) // TODO Handle malformed URLs properly

    if (key) {
      store.dispatch('auth/AUTHENTICATE', key).then(() => {
        store.dispatch('auth/GET_USER')
        next()
        // Remove the key from the path
        window.history.replaceState(null, null, to.path)
      })
      return
    }

    // If there was no key retrieve access token and expiry from session storage
    const accessToken = JSON.parse(sessionStorage.getItem('access_token'))
    const accessTokenExpiry = parseInt(sessionStorage.getItem('access_token_expiry') || 0, 10)

    // Check we have an access token and expiry and that access_token and expires_in are set
    if (!accessTokenExpiry || !accessToken || !accessToken.hasOwnProperty('access_token') || !accessToken.hasOwnProperty('expires_in')) {
      next({ name: 'PageError', params: { errorMessage: 'not authorised', errorCode: 403 } })
      return
    }

    // Check if the access token has expired
    if (Date.now() >= accessTokenExpiry) {
      next({ name: 'PageError', params: { errorMessage: 'session expired', errorCode: 403 } })
      return
    }

    // Set the access token for making requests to the API
    store.dispatch('auth/SET_AUTHENTICATION_VALUES', accessToken.access_token)
    store.dispatch('auth/GET_USER')
    next()
    return
  }

  if (to.meta.requiresPreviewAuth && !store.getters['auth/isAuthenticated']) {
    const key = to.hash.substring(1) // TODO Handle malformed URLs properly
    store.dispatch('auth/AUTHENTICATE_PREVIEW', key).then(() => {
      store.dispatch('auth/GET_USER')
      next()
      // Remove the key from the path
      window.history.replaceState(null, null, to.path)
    })
    return
  }

  next()
})

export default router
